import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Card, CardBody, Col } from "react-bootstrap";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { JobCategoriesService } from "../jobCategories/service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import CustomTooltip from "../../shared/components/CustomTooltip";

const RecommendedCategoriesList: React.FC = () => {
  type Recommender = { type: string; id: number; name: string };
  type Recommended = { name: string; data: Recommender[] };

  const [recommendedCategories, setRecommendedCategories] = useState<Recommended[]>(
    []
  );
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Recommended | null>(null);
  const [selectedLevel1, setSelectedLevel1] = useState<any | null>(null);
  const [selectedLevel2, setSelectedLevel2] = useState<any | null>(null);
  const [selectedLevel3, setSelectedLevel3] = useState<any | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<"approve" | "decline" | "delete">("approve");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCategories, setFilteredCategories] = useState<Recommended[]>([]);
  const { t } = useTranslation();

  // Normalize backend response to an array of { name, data }
  const normalizeRecommended = (resp: any): Recommended[] => {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    // If backend wraps array in { data: [...] }
    if (resp.data && Array.isArray(resp.data)) return resp.data;
    // If backend returns an object keyed by name -> data array
    if (typeof resp === "object") {
      try {
        return Object.keys(resp).map((key) => ({ name: key, data: resp[key] } as Recommended));
      } catch (err) {
        return [];
      }
    }
    return [];
  };

  useEffect(() => {
    // Fetch recommended categories (new structure: [{ name, data: [...] }])
    JobCategoriesService.getRecommendedCategories()
      .then((response: any) => {
        const normalized = normalizeRecommended(response);
        setRecommendedCategories(normalized);
        setFilteredCategories(normalized);
      })
      .catch((error) => ErrorToast(error));
  }, []);

  const handleApproveClick = (category: Recommended) => {
    setSelectedCategory(category);
    setModalType("approve");
    setShowModal(true);
    setSelectedLevel1(null);
    setSelectedLevel2(null);
    setSelectedLevel3(null);

    // Fetch all categories
    JobCategoriesService.getAll()
      .then((response) => {
        setCategories(response); // Use the hierarchical structure provided by the API
      })
      .catch((error) => ErrorToast(error));
  };

  const handleDeclineClick = (category: Recommended) => {
    setSelectedCategory(category);
    setModalType("decline");
    setShowModal(true);
    setSelectedLevel1(null);
    setSelectedLevel2(null);
    setSelectedLevel3(null);

    // Fetch all categories
    JobCategoriesService.getAll()
      .then((response) => {
        setCategories(response); // Use the hierarchical structure provided by the API
      })
      .catch((error) => ErrorToast(error));
  };

  const handleDeleteClick = (category: Recommended) => {
    setSelectedCategory(category);
    setModalType("delete");
    setShowModal(true);
  };

  const handleLevel1Change = (level1: any) => {
    setSelectedLevel1(level1);
    setSelectedLevel2(null); // Reset level 2 selection
    setSelectedLevel3(null); // Reset level 3 selection
  };

  const handleLevel2Change = (level2: any) => {
    setSelectedLevel2(level2);
    setSelectedLevel3(null); // Reset level 3 selection
  };

  const handleLevel3Change = (level3: any) => {
    setSelectedLevel3(level3);
  };

  const handleSubmit = () => {
    if (modalType === "approve") {
      // For approve, we need Level 2 (as per original logic)
      if (selectedLevel2 && selectedCategory) {
        JobCategoriesService.approveRecommendedCategory({
          name: selectedCategory.name,
          parent: selectedLevel2.id,
          isAccepted: true,
        })
          .then(() => {
            SuccessToast("Category approved successfully");
            setShowModal(false);
            setRecommendedCategories((prev) =>
              prev.filter((cat) => cat.name !== selectedCategory.name)
            );
            setFilteredCategories((prev) =>
              prev.filter((cat) => cat.name !== selectedCategory.name)
            );
          })
          .catch((error) => ErrorToast(error));
      }
    } else if (modalType === "decline") {
      // For decline, allow any level (1st, 2nd, or 3rd)
      const targetCategory = selectedLevel3 || selectedLevel2 || selectedLevel1;

      if (targetCategory && selectedCategory) {
        JobCategoriesService.declineRecommendedCategory({
          name: selectedCategory.name,
          category: targetCategory.id,
        })
          .then(() => {
            SuccessToast("Category declined successfully");
            setShowModal(false);
            setRecommendedCategories((prev) =>
              prev.filter((cat) => cat.name !== selectedCategory.name)
            );
            setFilteredCategories((prev) =>
              prev.filter((cat) => cat.name !== selectedCategory.name)
            );
          })
          .catch((error) => ErrorToast(error));
      }
    } else if (modalType === "delete") {
      // Call delete API via service
      if (selectedCategory) {
        JobCategoriesService.deleteRecommendedSuggestion({ name: selectedCategory.name })
          .then(() => {
            SuccessToast("Suggestion deleted successfully");
            setShowModal(false);
            setRecommendedCategories((prev) =>
              prev.filter((cat) => cat.name !== selectedCategory.name)
            );
            setFilteredCategories((prev) =>
              prev.filter((cat) => cat.name !== selectedCategory.name)
            );
          })
          .catch((error) => ErrorToast(error));
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    // Ensure recommendedCategories is an array before filtering
    const base = Array.isArray(recommendedCategories) ? recommendedCategories : [];
    setFilteredCategories(
      base.filter((category) => category.name && category.name.toLowerCase().includes(query))
    );
  };

  const MAX_RECOMMENDERS_CHARS = 80;
  const renderRecommenders = (category: Recommended) => {
    if (!category.data || category.data.length === 0) return t("recommendedCategories.noData");
    const full = category.data.map((d) => `${d.name} (${d.type})`).join(", ");
    if (full.length <= MAX_RECOMMENDERS_CHARS) return full;
    const truncated = full.slice(0, MAX_RECOMMENDERS_CHARS).trim() + "…";
    return <CustomTooltip text={full}>{truncated}</CustomTooltip>;
  };

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody className="card-body">
            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline ms-auto my-1 mt-3">
                <li className="list-inline-item">
                  <form className="form-search">
                    <Form.Control
                      type="search"
                      placeholder={t("recommendedCategories.searchPlaceholder")}
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                  </form>
                </li>
              </ul>
            </div>
            <div className="table-responsive">
              <table className="table table-hover sticky-actions">
                <thead>
                  <tr>
                    <th>{t("recommendedCategories.table.number")}</th>
                    <th>{t("recommendedCategories.table.category")}</th>
                    <th>{t("recommendedCategories.table.recommendedBy")}</th>
                    <th className="text-end sticky-col">{t("recommendedCategories.table.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories?.map((category, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td className="wrap-col">{category.name}</td>
                      <td>
                        {renderRecommenders(category)}
                      </td>
                      <td className="text-end text-nowrap sticky-col">
                        <Button
                          variant="success"
                          size="sm"
                          className="me-2"
                          onClick={() => handleApproveClick(category)}
                        >
                          {t("recommendedCategories.buttons.approve")}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          className="me-2"
                          onClick={() => handleDeclineClick(category)}
                        >
                          {t("recommendedCategories.buttons.decline")}
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteClick(category)}
                        >
                          {t("recommendedCategories.buttons.delete")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </Col>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === "approve"
              ? t("recommendedCategories.modal.approveTitle")
              : modalType === "decline"
                ? t("recommendedCategories.modal.declineTitle")
                : t("recommendedCategories.modal.deleteTitle")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            {modalType !== "delete" ? (
              <>
                <p>
                  {modalType === "approve"
                    ? t("recommendedCategories.modal.approveBody")
                    : t("recommendedCategories.modal.declineBody")}
                </p>
                <p>
                  <strong>{t("recommendedCategories.modal.categoryLabel")}</strong> "{selectedCategory?.name}"
                </p>
                <div className="mb-3">
                  <label className="form-label">{t("recommendedCategories.modal.level1")}</label>
                  <Select
                    options={categories.filter((cat) => cat.level === 1)}
                    getOptionLabel={(option) => option.title}
                    getOptionValue={(option) => option.id}
                    onChange={handleLevel1Change}
                    value={selectedLevel1}
                    placeholder={t("recommendedCategories.modal.selectLevel1")}
                  />
                </div>
                {selectedLevel1 && (
                  <div className="mb-3">
                    <label className="form-label">{t("recommendedCategories.modal.level2")} {modalType === "decline" ? t("recommendedCategories.modal.optional") : ""}</label>
                    <Select
                      options={selectedLevel1.children}
                      getOptionLabel={(option) => option.title}
                      getOptionValue={(option) => option.id}
                      onChange={handleLevel2Change}
                      value={selectedLevel2}
                      placeholder={t("recommendedCategories.modal.selectLevel2")}
                      isClearable={modalType === "decline"}
                    />
                  </div>
                )}
                {selectedLevel2 && modalType === "decline" && (
                  <div className="mb-3">
                    <label className="form-label">{t("recommendedCategories.modal.level3")}</label>
                    <Select
                      options={selectedLevel2.children}
                      getOptionLabel={(option) => option.title}
                      getOptionValue={(option) => option.id}
                      onChange={handleLevel3Change}
                      value={selectedLevel3}
                      placeholder={t("recommendedCategories.modal.selectLevel3")}
                      isClearable={true}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <p>{t("recommendedCategories.modal.deleteBody")}</p>
                <p>
                  <strong>{t("recommendedCategories.modal.categoryLabel")}</strong> "{selectedCategory?.name}"
                </p>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            {t("recommendedCategories.buttons.cancel")}
          </Button>
          <Button
            variant={modalType === "approve" ? "success" : "danger"}
            onClick={handleSubmit}
            disabled={
              modalType === "approve"
                ? !selectedLevel2
                : modalType === "decline"
                  ? !selectedLevel1
                  : false
            }
          >
            {modalType === "approve"
              ? t("recommendedCategories.modal.confirmApprove")
              : modalType === "decline"
                ? t("recommendedCategories.modal.confirmDecline")
                : t("recommendedCategories.modal.confirmDelete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default RecommendedCategoriesList;
